import { useCallback, useState } from 'react';

import { Backup as BackupIcon } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SelectedFiles } from './SelectedFiles';

const StyledBox = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    alignitems: center;
    width: 100w;
`;

const StyledCloudIcon = styled(BackupIcon)`
    && {
        margin-bottom: 5px;
        font-size: 40px;
        color: ${({ theme }) => theme.colors.primary.main};
    }
`;

const StyledInputBox = styled(Box)`
    border: 2px dashed gray;
    padding: 4px;
    margin-top: 8px;
    text-align: center;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 240px;
`;

const StyledDropZone = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    height: 88px;
    width: 320px;
`;

export type ExtendedFile = {
    uploading?: boolean;
    progress?: number;
    failed?: boolean;
    file: File;
};

export type SetSelectedFiles = React.Dispatch<React.SetStateAction<ExtendedFile[]>>;

export type FileDragDropProps = {
    acceptedFileTypesText?: string;
    acceptedFileTypes?: string[];
    onFilesAdded?: (files: ExtendedFile[], setSelectedFiles: SetSelectedFiles) => void;
};

export const FileDragDrop = (props: FileDragDropProps) => {
    const { acceptedFileTypesText, acceptedFileTypes, onFilesAdded } = props;
    const { t } = useTranslation('files');

    const [selectedFiles, setSelectedFiles] = useState<ExtendedFile[]>([]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            // Do something with the files
            const newlySelected: ExtendedFile[] = [];
            if (acceptedFiles && acceptedFiles.length > 0) {
                for (const file of acceptedFiles) {
                    if (acceptedFileTypes?.length) {
                        if (!acceptedFileTypes.includes(file.type.toLowerCase())) {
                            continue;
                        }
                    }
                    if (!selectedFiles.some((f) => f.file.name === file.name)) {
                        const extendedFile = { file };
                        newlySelected.push(extendedFile);
                    }
                }
            }
            if (newlySelected.length) {
                const newFilesArr = [...selectedFiles, ...newlySelected];
                setSelectedFiles(newFilesArr);
                if (onFilesAdded) {
                    onFilesAdded(newFilesArr, setSelectedFiles);
                }
            }
        },
        [selectedFiles, acceptedFileTypes, onFilesAdded]
    );
    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    return (
        <StyledBox>
            <StyledInputBox {...getRootProps()}>
                <input {...getInputProps()} />
                <StyledCloudIcon />
                <StyledDropZone>
                    <Typography variant="body2">{t('dragDropUpload.drag-drop-message')}</Typography>
                    {acceptedFileTypesText && <Typography variant="caption">{acceptedFileTypesText}</Typography>}
                    <Button variant="contained" sx={{ height: '36px' }}>
                        {t('dragDropUpload.browse')}
                    </Button>
                </StyledDropZone>
            </StyledInputBox>

            <SelectedFiles files={selectedFiles} />
        </StyledBox>
    );
};

export default FileDragDrop;
